import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';

export default function GestionLogements() {
    const [chambres, setChambres] = useState([]);
    const [demandes, setDemandes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showChambreForm, setShowChambreForm] = useState(false);
    const [searchEtudiant, setSearchEtudiant] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [nonLus, setNonLus] = useState(0);
    const [searching, setSearching] = useState(false);
    
    const [formChambre, setFormChambre] = useState({
        bloc: '',
        numero: '',
        capacite: 1,
        description: ''
    });

    const API_BASE = config.apiBase;

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            
            const chambresRes = await fetch(`${API_BASE}/chambres/liste.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const chambresData = await chambresRes.json();
            if (chambresData.success) setChambres(chambresData.chambres);
            
            const demandesRes = await fetch(`${API_BASE}/charge_logement/demandes.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const demandesData = await demandesRes.json();
            if (demandesData.success) {
                // Normaliser les URLs des documents
                const demandesNormalisees = (demandesData.demandes || []).map(d => ({
                    ...d,
                    photo_url: config.normalizeDocument(d.photo_url),
                    quittance_url: config.normalizeDocument(d.quittance_url)
                }));
                setDemandes(demandesNormalisees);
                setNonLus(demandesNormalisees.filter(d => d.statut === 'en_attente').length);
            }
        } catch (error) {
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 30000);
        return () => clearInterval(interval);
    }, []);

    // Recherche en temps réel
    useEffect(() => {
        const rechercherEnDirect = async () => {
            if (searchEtudiant.length < 2) {
                setSearchResults([]);
                setShowResults(false);
                return;
            }
            
            setSearching(true);
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_BASE}/charge_logement/rechercher_etudiant.php?q=${encodeURIComponent(searchEtudiant)}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                
                if (data.success && data.resultats && data.resultats.length > 0) {
                    setSearchResults(data.resultats);
                    setShowResults(true);
                } else {
                    setSearchResults([]);
                    setShowResults(true);
                }
            } catch (error) {
                console.error('Erreur:', error);
                setSearchResults([]);
            } finally {
                setSearching(false);
            }
        };
        
        const delayDebounce = setTimeout(() => {
            rechercherEnDirect();
        }, 300);
        
        return () => clearTimeout(delayDebounce);
    }, [searchEtudiant]);

    const ajouterChambre = async () => {
        if (!formChambre.bloc || !formChambre.numero) {
            toast.error('Bloc et numéro requis');
            return;
        }
        
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/charge_logement/ajouter_chambre.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formChambre)
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Chambre ajoutée');
                setShowChambreForm(false);
                setFormChambre({ bloc: '', numero: '', capacite: 1, description: '' });
                fetchData();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const supprimerChambre = async (id) => {
        if (!window.confirm('Supprimer cette chambre ?')) return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/charge_logement/supprimer_chambre.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id_chambre: id })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Chambre supprimée');
                fetchData();
            }
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const validerDemande = async (id, action) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/charge_logement/traiter_demande.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id_demande: id, action })
            });
            const data = await response.json();
            if (data.success) {
                toast.success(`Demande ${action === 'valider' ? 'validée' : 'rejetée'}`);
                setSelectedDemande(null);
                fetchData();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const occupationTotale = chambres.reduce((acc, c) => acc + c.occupation, 0);
    const capaciteTotale = chambres.reduce((acc, c) => acc + c.capacite, 0);
    const tauxOccupation = capaciteTotale > 0 ? (occupationTotale / capaciteTotale) * 100 : 0;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
            <div className="mx-auto max-w-7xl">
                {/* Header avec notifications */}
                <div className="mb-8 rounded-2xl bg-gradient-to-r from-green-600 to-teal-600 p-6 text-white shadow-xl">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold">🏢 Gestion des logements</h1>
                            <p className="mt-2 opacity-90">
                                Gérez les chambres et les demandes de logement des étudiants
                            </p>
                        </div>
                        {nonLus > 0 && (
                            <div className="relative">
                                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-yellow-500 animate-pulse">
                                    <span className="text-2xl font-bold">{nonLus}</span>
                                </div>
                                <div className="absolute -right-2 -top-2">
                                    <span className="flex h-3 w-3">
                                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
                                        <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500"></span>
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Statistiques */}
                <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
                    <div className="rounded-xl bg-white p-4 shadow-md">
                        <p className="text-sm text-slate-500">Chambres</p>
                        <p className="text-2xl font-bold text-slate-800">{chambres.length}</p>
                    </div>
                    <div className="rounded-xl bg-white p-4 shadow-md">
                        <p className="text-sm text-slate-500">Occupées</p>
                        <p className="text-2xl font-bold text-green-600">{occupationTotale}</p>
                    </div>
                    <div className="rounded-xl bg-white p-4 shadow-md">
                        <p className="text-sm text-slate-500">Capacité totale</p>
                        <p className="text-2xl font-bold text-blue-600">{capaciteTotale}</p>
                    </div>
                    <div className="rounded-xl bg-white p-4 shadow-md">
                        <p className="text-sm text-slate-500">Taux occupation</p>
                        <p className="text-2xl font-bold text-purple-600">{tauxOccupation.toFixed(1)}%</p>
                    </div>
                </div>

                {/* Recherche d'étudiant en temps réel */}
                <div className="mb-6 rounded-xl bg-white p-4 shadow-md">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="🔍 Rechercher un étudiant (minimum 2 lettres)..."
                            value={searchEtudiant}
                            onChange={(e) => setSearchEtudiant(e.target.value)}
                            className="w-full rounded-xl border border-slate-200 px-4 py-3 pr-10 outline-none focus:border-teal-400"
                            autoComplete="off"
                        />
                        {searching && (
                            <div className="absolute right-3 top-3">
                                <div className="h-5 w-5 animate-spin rounded-full border-2 border-teal-600 border-t-transparent"></div>
                            </div>
                        )}
                    </div>
                    
                    {/* Résultats de recherche en direct */}
                    {showResults && searchEtudiant.length >= 2 && (
                        <div className="mt-3 max-h-80 overflow-y-auto rounded-xl border border-slate-200 bg-white">
                            {searchResults.length === 0 ? (
                                <div className="p-4 text-center text-slate-500">
                                    ❌ Aucun étudiant trouvé pour "{searchEtudiant}"
                                </div>
                            ) : (
                                <div>
                                    <div className="sticky top-0 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-500 border-b">
                                        {searchResults.length} résultat(s) trouvé(s)
                                    </div>
                                    {searchResults.map((result, idx) => (
                                        <div key={idx} className="border-b border-slate-100 p-4 hover:bg-slate-50 transition">
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <p className="font-semibold text-slate-800">
                                                        {result.prenom} {result.nom}
                                                    </p>
                                                    <p className="text-sm text-slate-500">{result.email}</p>
                                                </div>
                                                <div className="text-right">
                                                    {result.bloc && result.numero ? (
                                                        <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-700">
                                                            🏠 {result.bloc}-{result.numero}
                                                        </span>
                                                    ) : (
                                                        <span className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-500">
                                                            Aucune chambre
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    
                    {searchEtudiant.length > 0 && searchEtudiant.length < 2 && (
                        <p className="mt-2 text-xs text-slate-400">
                            🔍 Entrez au moins 2 lettres pour lancer la recherche
                        </p>
                    )}
                </div>

                {/* Gestion des chambres */}
                <div className="mb-8">
                    <div className="mb-4 flex items-center justify-between">
                        <h2 className="text-xl font-bold text-slate-800">🏠 Chambres</h2>
                        <button
                            onClick={() => setShowChambreForm(!showChambreForm)}
                            className="rounded-xl bg-green-600 px-4 py-2 text-white transition hover:bg-green-700"
                        >
                            + Ajouter une chambre
                        </button>
                    </div>

                    {showChambreForm && (
                        <div className="mb-6 rounded-xl bg-white p-4 shadow-md">
                            <div className="grid gap-4 md:grid-cols-4">
                                <input
                                    type="text"
                                    placeholder="Bloc"
                                    value={formChambre.bloc}
                                    onChange={(e) => setFormChambre({ ...formChambre, bloc: e.target.value })}
                                    className="rounded-xl border border-slate-200 px-3 py-2"
                                />
                                <input
                                    type="text"
                                    placeholder="Numéro"
                                    value={formChambre.numero}
                                    onChange={(e) => setFormChambre({ ...formChambre, numero: e.target.value })}
                                    className="rounded-xl border border-slate-200 px-3 py-2"
                                />
                                <input
                                    type="number"
                                    placeholder="Capacité"
                                    value={formChambre.capacite}
                                    onChange={(e) => setFormChambre({ ...formChambre, capacite: parseInt(e.target.value) })}
                                    className="rounded-xl border border-slate-200 px-3 py-2"
                                />
                                <button
                                    onClick={ajouterChambre}
                                    className="rounded-xl bg-blue-600 py-2 text-white"
                                >
                                    Ajouter
                                </button>
                            </div>
                            <textarea
                                placeholder="Description"
                                value={formChambre.description}
                                onChange={(e) => setFormChambre({ ...formChambre, description: e.target.value })}
                                className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-2"
                                rows="2"
                            />
                        </div>
                    )}

                    <div className="overflow-x-auto rounded-xl bg-white shadow-md">
                        <table className="min-w-full">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-semibold">Bloc</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold">Chambre</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold">Occupation</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold">Capacité</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {chambres.map((chambre) => (
                                    <tr key={chambre.id_chambre} className="hover:bg-slate-50">
                                        <td className="px-4 py-3">{chambre.bloc}</td>
                                        <td className="px-4 py-3">{chambre.numero}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="h-2 flex-1 rounded-full bg-slate-200">
                                                    <div 
                                                        className="h-2 rounded-full bg-green-500"
                                                        style={{ width: `${(chambre.occupation / chambre.capacite) * 100}%` }}
                                                    ></div>
                                                </div>
                                                <span>{chambre.occupation}/{chambre.capacite}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">{chambre.capacite}</td>
                                        <td className="px-4 py-3">
                                            <button
                                                onClick={() => supprimerChambre(chambre.id_chambre)}
                                                className="text-red-500 hover:text-red-700"
                                            >
                                                🗑️
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Demandes en attente */}
                <div>
                    <h2 className="mb-4 text-xl font-bold text-slate-800">
                        📋 Demandes en attente {nonLus > 0 && `(${nonLus})`}
                    </h2>
                    
                    {demandes.filter(d => d.statut === 'en_attente').length === 0 ? (
                        <div className="rounded-xl bg-white p-8 text-center">
                            <p className="text-slate-500">Aucune demande en attente</p>
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            {demandes.filter(d => d.statut === 'en_attente').map((demande) => (
                                <div key={demande.id_demande} className="rounded-xl bg-white p-4 shadow-md">
                                    <div className="flex flex-wrap items-start justify-between gap-4">
                                        <div>
                                            <p className="font-semibold">{demande.etudiant_nom} {demande.etudiant_prenom}</p>
                                            <p className="text-sm text-slate-500">{demande.etudiant_email}</p>
                                            <p className="text-sm text-slate-500">Type: {demande.type_demande === 'postulation' ? '📝 Postulation' : '🔄 Renouvellement'}</p>
                                            {demande.chambre_demande && (
                                                <p className="text-sm text-slate-500">Chambre demandée: {demande.chambre_demande}</p>
                                            )}
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setSelectedDemande(demande)}
                                                className="rounded-xl bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700"
                                            >
                                                📄 Voir les documents
                                            </button>
                                            <button
                                                onClick={() => validerDemande(demande.id_demande, 'valider')}
                                                className="rounded-xl bg-green-600 px-4 py-2 text-white transition hover:bg-green-700"
                                            >
                                                ✅ Valider
                                            </button>
                                            <button
                                                onClick={() => validerDemande(demande.id_demande, 'rejeter')}
                                                className="rounded-xl bg-red-600 px-4 py-2 text-white transition hover:bg-red-700"
                                            >
                                                ❌ Rejeter
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Modal d'aperçu des documents */}
                {selectedDemande && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
                        <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6">
                            <div className="mb-4 flex items-center justify-between">
                                <h3 className="text-xl font-bold">Documents de {selectedDemande.etudiant_nom}</h3>
                                <button
                                    onClick={() => setSelectedDemande(null)}
                                    className="text-2xl text-slate-400 hover:text-slate-600"
                                >
                                    ✕
                                </button>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <p className="font-semibold">Carte d'identité</p>
                                    <img src={selectedDemande.photo_url} alt="Carte" className="mt-2 max-h-64 rounded-lg border" />
                                </div>
                                <div>
                                    <p className="font-semibold">Quittance d'inscription</p>
                                    <img src={selectedDemande.quittance_url} alt="Quittance" className="mt-2 max-h-64 rounded-lg border" />
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end gap-3">
                                <button
                                    onClick={() => setSelectedDemande(null)}
                                    className="rounded-xl bg-slate-200 px-4 py-2"
                                >
                                    Fermer
                                </button>
                                <button
                                    onClick={() => {
                                        validerDemande(selectedDemande.id_demande, 'valider');
                                        setSelectedDemande(null);
                                    }}
                                    className="rounded-xl bg-green-600 px-4 py-2 text-white"
                                >
                                    ✅ Valider cette demande
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}